import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import * as chrono from 'chrono-node';
import { CalendarIcon } from 'lucide-react';
import {
  ValidationResult as AriaValidationResult,
  Dialog,
} from 'react-aria-components';

import { useTranslations } from '../../../i18n/useTranslations';
import { cn } from '../../../lib/stylingUtils';
import { FieldError, FieldGroup, Label } from '../Field';
import { Popover } from '../overlays/Popover';

import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarHeading,
} from './Calendar';

interface SmartDatePickerProps {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: AriaValidationResult) => string);
  placeholder?: string;
  dateFormat?: Intl.DateTimeFormatOptions;
  locale?: string;
  value?: CalendarDate | null;
  defaultValue?: CalendarDate | null;
  onChange?: (date: CalendarDate | undefined) => void;
  onBlur?: () => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  isReadOnly?: boolean;
  isInvalid?: boolean;
  className?: string;
  minValue?: CalendarDate | null;
  maxValue?: CalendarDate | null;
  autoFocus?: boolean;
}

let placeholderIndex = 0;

const SmartDatePicker = ({
  className,
  label,
  description,
  errorMessage,
  placeholder: placeholderProp,
  dateFormat = { year: 'numeric', month: 'short', day: 'numeric' },
  locale = 'en-US',
  value,
  defaultValue,
  onChange,
  onBlur,
  minValue,
  maxValue,
  autoFocus = false,
  isDisabled = false,
  isRequired = false,
  isReadOnly = false,
}: SmartDatePickerProps) => {
  const t = useTranslations();

  const placeholder = React.useMemo(() => {
    if (placeholderProp) {
      return placeholderProp;
    }

    return [
      'e.g. tomorrow, next wed, 5/23, jan 7...',
      'e.g. tmw, 30d, sat, jan 7, 5/23...',
    ][placeholderIndex++ % 2];
  }, [placeholderProp]);

  const [isInvalid, setIsInvalid] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const fieldGroupRef = React.useRef<HTMLDivElement>(null);

  const [inputValue, setInputValue] = React.useState('');
  const [showCalendar, setShowCalendar] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  const dateFormatRef = React.useRef(dateFormat);
  dateFormatRef.current = dateFormat;

  // Format the current value for display
  const formatDate = React.useCallback(
    (date: Date) =>
      new Intl.DateTimeFormat(locale, {
        ...dateFormatRef.current,
        timeZone: 'UTC',
      }).format(date),
    [locale],
  );

  // Initialize with default value or value
  React.useEffect(() => {
    const initialValue = value || defaultValue;
    if (initialValue) {
      const date = initialValue.toDate('UTC');
      setInputValue(formatDate(date));
    }
  }, [value, defaultValue, formatDate]);

  const inputValueRef = React.useRef(inputValue);
  inputValueRef.current = inputValue;

  // Update input value when external value changes (but not when user is editing)
  React.useEffect(() => {
    if (!inputValueRef.current.trim()) {
      if (value) {
        const date = value.toDate('UTC');
        setInputValue(formatDate(date));
      } else if (value === null) {
        setInputValue('');
      }
    }
  }, [value, formatDate]);

  // Parse natural language date input
  const parseNaturalLanguage = React.useCallback(
    (text: string) => {
      if (!text.trim()) {
        return null;
      }

      const forwardDate = !!minValue;
      const dateReference = minValue ? minValue.toDate('UTC') : new Date();

      const results = chrono.parse(
        text
          .replace(/tmw/i, 'tomorrow')
          .replace(/yday/i, 'yesterday')
          .replace(/yd/i, 'yesterday')
          .replace(/tday/i, 'today')
          .replace(/tdy/i, 'today')
          .replace(/td/i, 'today'),
        {
          instant: dateReference,
          timezone: 'UTC',
        },
        {
          forwardDate: forwardDate,
        },
      );
      if (results.length > 0) {
        return results[0].start.date();
      }

      return null;
    },
    [minValue],
  );

  // Handle input change
  const handleInputChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      setIsInvalid(false);
      setIsEditing(true);
    },
    [],
  );

  // Handle input blur or enter key
  const handleInputComplete = React.useCallback(() => {
    const date = parseNaturalLanguage(inputValue);

    let isWithinRange = false;

    if (date && minValue && maxValue) {
      isWithinRange =
        +minValue.toDate('UTC') <= +date && +date <= +maxValue.toDate('UTC');
    } else if (date && minValue) {
      isWithinRange = +minValue.toDate('UTC') <= +date;
    } else if (date && maxValue) {
      isWithinRange = +date <= +maxValue.toDate('UTC');
    } else if (date) {
      isWithinRange = true;
    }

    setIsEditing(false);

    if (date && isWithinRange) {
      const formattedDate = formatDate(date);
      setInputValue(formattedDate);
      setIsInvalid(false);

      // Convert to CalendarDate
      if (onChange) {
        const [year, month, day] = date.toISOString().slice(0, 10).split('-');
        const calendarDate = new CalendarDate(
          Number(year),
          Number(month),
          Number(day),
        );
        onChange(calendarDate);
      }
    } else if (inputValue.trim()) {
      setIsInvalid(true);
    }
  }, [
    formatDate,
    inputValue,
    maxValue,
    minValue,
    onChange,
    parseNaturalLanguage,
  ]);

  // Handle keyboard events
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        handleInputComplete();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        setShowCalendar(true);
      }
    },
    [handleInputComplete, setShowCalendar],
  );

  const handleBlur = React.useCallback(() => {
    setIsEditing(false);
    handleInputComplete();
    onBlur?.();
  }, [handleInputComplete, onBlur]);

  const isNextYear = React.useMemo(
    () => value?.toDate('UTC').getFullYear() === new Date().getFullYear() + 1,
    [value],
  );

  const shouldShowNextYearWarning = React.useMemo(
    () =>
      inputValue.trim() && isNextYear && !!minValue && !isInvalid && !isEditing,
    [inputValue, isEditing, isInvalid, isNextYear, minValue],
  );

  const shouldShowInvalidWarning = React.useMemo(
    () => inputValue.trim() && isInvalid && !isEditing,
    [inputValue, isEditing, isInvalid],
  );

  return (
    <div
      className={cn('@SmartDatePicker', 'group flex flex-col gap-2', className)}
    >
      {label && <Label aria-required={isRequired}>{label}</Label>}

      <FieldGroup
        ref={fieldGroupRef}
        isInvalid={isInvalid}
        className="relative gap-2 pr-3.5"
      >
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          type="text"
          className="mr-2 flex-1 bg-transparent outline-none placeholder:pb-2 placeholder:text-[11px]"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          readOnly={isReadOnly}
          required={isRequired}
          aria-invalid={isInvalid}
        />
        <div
          className={
            'pointer-events-none absolute left-0 px-3 text-transparent'
          }
        >
          {inputValue}{' '}
          {shouldShowNextYearWarning && (
            <span className={'text-[11px] text-warning'}>next year</span>
          )}
          {shouldShowInvalidWarning && (
            <span className={'text-[11px] text-failure'}>invalid</span>
          )}
        </div>
        <button
          tabIndex={-1}
          type="button"
          className={cn(
            '-m-2 rounded-sm border-none p-1 text-primary outline-none hover:bg-primary/20 focus:bg-primary/20 focus-visible:outline-primary/20',
            isInvalid && 'text-failure',
          )}
          aria-label={t.components.datePicker.openCalendar}
          disabled={isDisabled || isReadOnly}
          onClick={(e) => {
            e.preventDefault();
            setShowCalendar(true);
          }}
        >
          <CalendarIcon className="size-4" />
        </button>
        <Popover
          triggerRef={fieldGroupRef}
          placement="bottom left"
          className="p-3"
          isOpen={showCalendar}
          onOpenChange={setShowCalendar}
        >
          <Dialog className="outline-none">
            <Calendar
              autoFocus
              minValue={minValue}
              maxValue={maxValue}
              value={value}
              onChange={(newDate: CalendarDate) => {
                onChange?.(newDate);
                setShowCalendar(false);
              }}
            >
              <CalendarHeading />
              <CalendarGrid>
                <CalendarGridHeader>
                  {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => <CalendarCell date={date} />}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </Dialog>
        </Popover>
      </FieldGroup>

      {description && (
        <div className="text-sm text-neutral/40">{description}</div>
      )}
      {errorMessage && <FieldError>{errorMessage}</FieldError>}
    </div>
  );
};

export { SmartDatePicker };
export type { SmartDatePickerProps };
